import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import logger from './../utils/logger.js';

const SubscriptionContext = createContext();

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

export const SubscriptionProvider = ({ children }) => {
    const { user, authLoading } = useAuth(); // Get auth state
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [lushaCredits, setLushaCredits] = useState(null);

    const checkSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/subscription/status');
            logger.log('Subscription status response:', response.data);
            if (response.data.success) {
                setSubscriptionStatus(response.data.data);
            }
        } catch (error) {
            logger.error('Error checking subscription status:', error);
            // If user is not authenticated, set basic status
            setSubscriptionStatus({
                has_subscription: false,
                status: 'none',
                needs_subscription: true,
                trial_expired: false,
                days_remaining: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchPlans = async () => {
        try {
            const response = await api.get('/api/subscription/plans');
            logger.log('Subscription plans response:', response.data);
            if (response.data.success) {
                // Extract the plans array from the nested data structure
                setPlans(response.data.data.plans || []);
            }
        } catch (error) {
            logger.error('Error fetching subscription plans:', error);
            // Set empty array on error to prevent map function errors
            setPlans([]);
        }
    };

    const fetchLushaCredits = async () => {
        try {
            const response = await api.get('/api/subscription/lusha-credits');
            if (response.data.success) {
                setLushaCredits(response.data.data);
            }
        } catch (error) {
            logger.error('Error fetching Lusha credits:', error);
        }
    };

    const activateSubscription = async (planId, paymentMethod, paymentToken) => {
        try {
            const response = await api.post('/api/subscription/activate', {
                plan_id: planId,
                payment_method: paymentMethod,
                payment_token: paymentToken
            });
            
            if (response.data.success) {
                // Refresh subscription status after activation
                await checkSubscriptionStatus();
                return { success: true, message: response.data.message };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            logger.error('Error activating subscription:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to activate subscription' 
            };
        }
    };

    const cancelSubscription = async (reason) => {
        try {
            const response = await api.post('/api/subscription/cancel', { reason });
            
            if (response.data.success) {
                // Refresh subscription status after cancellation
                await checkSubscriptionStatus();
                return { success: true, message: response.data.message };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            logger.error('Error cancelling subscription:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to cancel subscription' 
            };
        }
    };

    const hasAccess = () => {
        if (!subscriptionStatus) return false;
        return subscriptionStatus.status?.has_access || false;
    };

    const isTrialExpired = () => {
        if (!subscriptionStatus) return false;
        return subscriptionStatus.status?.is_trial_expired || false;
    };

    const needsSubscription = () => {
        if (!subscriptionStatus) return true;
        return subscriptionStatus.status?.needs_subscription || false;
    };

    const getDaysRemaining = () => {
        if (!subscriptionStatus) return 0;
        return subscriptionStatus.status?.days_remaining || 0;
    };

    const getTrialEndDate = () => {
        if (!subscriptionStatus?.trial_end_date) return null;
        return new Date(subscriptionStatus.trial_end_date);
    };

    const getSubscription = () => {
        return subscriptionStatus?.subscription || null;
    };

    useEffect(() => {
        // Only fetch subscription data if user is authenticated
        if (!authLoading && user) {
            checkSubscriptionStatus();
            fetchPlans();
        } else if (!authLoading && !user) {
            // Set default state for unauthenticated users
            setSubscriptionStatus({
                has_subscription: false,
                status: 'none',
                needs_subscription: true,
                trial_expired: false,
                days_remaining: 0
            });
            setPlans([]);
            setLoading(false);
        }
    }, [user, authLoading]);

    const value = {
        subscriptionStatus,
        loading,
        plans,
        lushaCredits,
        checkSubscriptionStatus,
        fetchPlans,
        fetchLushaCredits,
        activateSubscription,
        cancelSubscription,
        hasAccess,
        isTrialExpired,
        needsSubscription,
        getDaysRemaining,
        getTrialEndDate,
        getSubscription
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
}; 